import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams, Link } from 'react-router-dom'
import toast from 'react-hot-toast'
import { apiClient } from '@/lib/api'
import { useAuthStore } from '@/stores/authStore'
import LoadingSpinner from '@/components/ui/LoadingSpinner'

const SetupTwoFactor = () => {
  const [searchParams] = useSearchParams()
  const challengeId = searchParams.get('challenge') || ''
  const navigate = useNavigate()
  const { applySession } = useAuthStore()
  const [loading, setLoading] = useState(true)
  const [qrCode, setQrCode] = useState<string | null>(null)
  const [secret, setSecret] = useState<string | null>(null)
  const [token, setToken] = useState('')
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (!challengeId) {
      setLoading(false)
      return
    }
    ;(async () => {
      try {
        const res = await apiClient.post<{ success: boolean; data: { qr_code_data_url: string; secret: string } }>(
          '/auth/2fa/setup-by-challenge',
          { challenge_id: challengeId },
        )
        if (res.success && res.data) {
          setQrCode(res.data.qr_code_data_url)
          setSecret(res.data.secret)
        }
      } catch {
        toast.error('Invalid or expired setup link. Please sign in again.')
      } finally {
        setLoading(false)
      }
    })()
  }, [challengeId])

  const confirm = async (e: React.FormEvent) => {
    e.preventDefault()
    setBusy(true)
    try {
      const res = await apiClient.post<{
        success: boolean
        data: {
          token: string
          user: { id: number; email: string; name: string; role?: string; shop_id?: number; is_web_user?: boolean }
          recovery_codes?: string[]
        }
      }>('/auth/2fa/confirm-by-challenge', { challenge_id: challengeId, token })
      if (res.success && res.data) {
        applySession({ token: res.data.token, user: res.data.user })
        toast.success('Two-factor authentication is enabled')
        if (res.data.recovery_codes?.length) {
          toast('Save your recovery codes from the response in a secure place', { duration: 8000 })
        }
        navigate(res.data.user.is_web_user ? '/customer' : '/dashboard', { replace: true })
      }
    } catch (err: unknown) {
      const msg =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
          : undefined
      toast.error(msg || 'Failed to enable 2FA')
    } finally {
      setBusy(false)
    }
  }

  if (!challengeId) {
    return (
      <p className="text-sm text-gray-600">
        Missing setup link. <Link to="/auth/staff/login">Return to sign in</Link>
      </p>
    )
  }

  if (loading) return <LoadingSpinner />

  return (
    <div className="space-y-4">
      <h2 className="text-2xl font-bold text-gray-900">Set up two-factor authentication</h2>
      <p className="text-sm text-gray-600">Scan the QR code with your authenticator app, then enter a code to finish.</p>
      {qrCode && <img src={qrCode} alt="QR code" className="w-44 h-44" />}
      {secret && <p className="text-xs text-gray-500 font-mono break-all">Key: {secret}</p>}
      <form onSubmit={confirm} className="space-y-3">
        <input className="input" value={token} onChange={(e) => setToken(e.target.value)} placeholder="6-digit code" required />
        <button type="submit" className="btn btn-primary w-full" disabled={busy}>
          {busy ? 'Enabling…' : 'Enable and continue'}
        </button>
      </form>
    </div>
  )
}

export default SetupTwoFactor
